<template>
  <section class="form-section">
    <h1 class="form-section__title">
      {{ $t('PUBLISHER_ACCOUNT.COMPANY_INFO') }}
      <a
        v-if="$auth.check(['publisher', 'changeFinanceInfo']) && !isVerified"
        v-show="!isEditableCompanyInfo"
        class="form-section__action-btn"
        role="button"
        @click.prevent="isEditableCompanyInfo = true"
      >
        <i class="el-icon-edit form-section__action-btn-icon" />
        <span class="form-section__edit-title">
          {{ $t('EDIT') }}
        </span>
      </a>
    </h1>

    <div class="row align-justify">
      <div class="small-12 large-4 columns">
        <p class="form-section__description">
          <span v-if="!isVerified">
            {{ $t('PUBLISHER_ACCOUNT.COMPANY_INFO_DESC') }}
          </span>
          <RouterLinkFormat
            :links="{
              support: { to: { path: '/support' }, text: $t('SUPPORT') },
            }"
          >
            {{ $t('EDIT_DISABLED_DESC') }}
          </RouterLinkFormat>
        </p>
      </div>

      <div class="small-12 large-7 columns">
        <ElForm
          ref="companyInfo"
          class="card"
          :rules="rules"
          :model="legalInfoModel"
          label-position="top"
          @submit.native.prevent="updateCompanyInfo"
        >
          <div
            v-if="
              $auth.check(['publisher', 'changeFinanceInfo']) && !isVerified
            "
            v-show="isEditableCompanyInfo"
          >
            <div class="card__block">
              <ElFormItem
                :label="$t('PUBLISHER_ACCOUNT.COMPANY_CERTIFICATE')"
                required
              >
                <Droppable
                  v-if="hasProps(legalInfoModel)"
                  :has-preview="true"
                  :has-progress="true"
                  file-types="image/*"
                  :upload-url="uploadPaths.companyCertificate"
                  :image-url="legalInfoModel.company_certificate.url"
                  @uploaded="updateCertImage"
                >
                  <div slot="label">
                    {{ $t('PUBLISHER_ACCOUNT.DRAG_CERTIFICATION_IMAGE') }}
                  </div>
                </Droppable>
              </ElFormItem>

              <ElFormItem
                :label="$t('PUBLISHER_ACCOUNT.COMPANY_NAME')"
                prop="name"
              >
                <ElInput v-model="legalInfoModel.name" name="name" />
              </ElFormItem>

              <ElFormItem
                :label="$t('PUBLISHER_ACCOUNT.REGISTRATION_NUMBER')"
                prop="registration_number"
              >
                <ElInput
                  v-model="legalInfoModel.registration_number"
                  name="registration_number"
                />
              </ElFormItem>

              <ElButton type="primary" native-type="submit" size="large">
                {{ $t('SAVE') }}
              </ElButton>

              <ElButton
                type="text"
                @click.native="isEditableCompanyInfo = false"
              >
                {{ $t('CANCEL') }}
              </ElButton>
            </div>
          </div>

          <div v-show="!isEditableCompanyInfo">
            <div class="card__block">
              <dl class="list list--definition list--clean">
                <dt>{{ $t('PUBLISHER_ACCOUNT.COMPANY_CERTIFICATE') }}</dt>
                <dd>
                  <img
                    v-if="hasProps(legalInfo)"
                    class="droppable__image"
                    :src="legalInfo.company_certificate.url"
                  />
                </dd>

                <dt>{{ $t('PUBLISHER_ACCOUNT.COMPANY_NAME') }}</dt>
                <dd>{{ legalInfo.name }}</dd>

                <dt>{{ $t('PUBLISHER_ACCOUNT.REGISTRATION_NUMBER') }}</dt>
                <dd>{{ legalInfo.registration_number }}</dd>
              </dl>
            </div>
          </div>
        </ElForm>
      </div>
    </div>
  </section>
</template>

<script>
import Droppable from '@@/layout/Droppable';

import { Publishers } from '@/api/api';
import { nonEmptyProps, hasProps } from '@/utils/object';
import form from '@/mixins/form';

import RouterLinkFormat from '@@/common/RouterLinkFormat';

export default {
  components: {
    Droppable,
    RouterLinkFormat,
  },
  mixins: [form],

  props: {
    legalInfo: {
      type: Object,
      default() {
        return {};
      },
    },

    isVerified: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      legalInfoModel: Object.assign({}, this.legalInfo),
      uploadPaths: Publishers.uploadPaths,
      isEditableCompanyInfo: false,
    };
  },

  computed: {
    companyInfo() {
      const model = this.legalInfoModel;

      return nonEmptyProps({
        name: model.name,
        registration_number: model.registration_number,
        company_certificate: model.company_certificate.id,
      });
    },

    rules() {
      return {
        name: {
          required: true,
          message: this.$t('INVALID_INPUT_VALUE'),
        },
        registration_number: {
          required: true,
          message: this.$t('INVALID_INPUT_VALUE'),
        },
      };
    },
  },

  watch: {
    legalInfo() {
      this.legalInfoModel = Object.assign({}, this.legalInfo);
    },
  },

  methods: {
    hasProps,

    updateCertImage(src) {
      this.legalInfoModel.company_certificate.id = src;
    },

    updateCompanyInfo() {
      if (
        this.$auth.check(['publisher', 'changeFinanceInfo']) &&
        this.isValid('companyInfo')
      ) {
        return (
          Publishers.updateLegal({ type: 'identification' }, this.companyInfo)
            .then(
              ({
                body: { message, identification_info: identificationInfo },
              }) => {
                this.legalInfoModel = Object.assign({}, identificationInfo);

                this.$emit('updatedLegalInfo', this.legalInfoModel);
                this.$message.success(message);
              },
            )
            // switches to the read-only mode
            .then(() => {
              this.isEditableCompanyInfo = false;
            })
            .catch(({ body: { message } = {} }) => {
              if (message) {
                this.$message.error(message);
              }
            })
        );
      }
      return false;
    },
  },
};
</script>
